import { Modal } from "../../../shared/ui/Modal";
import { Typography } from "../../../shared/ui/Typography/Typography";
import { useState, useEffect, useMemo } from "react";
import styles from "./AddCompaniesTagsModal.module.scss";
import { NormalButton } from "../../../shared/ui/Button";
import clsx from "clsx";
import { patchBulkAssignTags } from "../model/services/patchBulkAssignTags";
import { getAllTagsForOrg } from "../model/services/getAllTagsForOrg";
import { DropdownInput } from "@/frontend/shared/ui/DropdownInput";
import { useQuery } from "@tanstack/react-query";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { removeTagFromCompany } from "@/frontend/components/AddCompaniesTagsModal/model/services/removeTagFromCompany";
import { DiscardChangesModal } from "@/frontend/shared/ui/DiscardChangesModal/DiscardChangesModal";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";

type TAddTagsModalProps = {
  isOpen: boolean;
  ids: number[];
  onClose: () => void;
  triggerRefetch: () => void;
  currentTags: string[];
};

type TTag = {
  id: number;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  organizationId: number;
};

export function AddCompaniesTagsModal({ isOpen, onClose, ids, triggerRefetch, currentTags }: TAddTagsModalProps) {
  const [oldTags, setOldTags] = useState<string[]>(currentTags);
  const [tagInput, setTagInput] = useState<string>("");
  const [newTags, setNewTags] = useState<string[]>([]);
  const [oldTagsToDelete, setOldTagsToDelete] = useState<string[]>([]);
  const [inputError, setInputError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [organizationTags, setOrganizationTags] = useState<{ label: string; value: string }[]>([]);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const { data: userData } = useMe();

  useEffect(() => {
    setOldTags(currentTags);
  }, [currentTags]);

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: () =>
      getAllTagsForOrg().then((tags) => {
        return tags;
      }),

    enabled: userData?.entity === ERoleAccess.INVESTOR
  });

  useEffect(() => {
    if (!tagsData) return;
    const formattedTags = tagsData.map((tag: TTag) => ({ label: tag.name, value: tag.name }));
    setOrganizationTags(formattedTags);
  }, [tagsData]);

  const displayedOldTags = useMemo(() => {
    if (oldTagsToDelete.length > 0) {
      return oldTags.filter((tag) => !oldTagsToDelete.includes(tag));
    }
    return oldTags;
  }, [oldTagsToDelete, oldTags]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        if (tagInput?.length === 0) return setInputError("Tag is too small");
        if (newTags.includes(tagInput) || displayedOldTags.includes(tagInput))
          return setInputError("Tag already exists");
        if (oldTagsToDelete.includes(tagInput)) {
          setOldTagsToDelete((prev) => prev.filter((tag) => tag !== tagInput));
        } else {
          setNewTags((prev) => [...prev, tagInput]);
        }
        setTagInput("");
        setInputError("");
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [tagInput, newTags, oldTagsToDelete, displayedOldTags]);

  async function assignTagsHandler() {
    const tagsToDeleteIds = oldTagsToDelete
      .map((tag) => tagsData?.find((t) => t.name === tag)?.id)
      .filter((id) => typeof id === "number"); // making it so for typescript
    if (newTags?.length === 0 && tagsToDeleteIds?.length === 0)
      return setInputError("To save tags you need to add or delete at least one tag");
    setIsLoading(true);
    try {
      if (newTags.length > 0) {
        await patchBulkAssignTags(ids, newTags);
      }
      if (tagsToDeleteIds.length > 0) {
        await removeTagFromCompany({ tagIds: tagsToDeleteIds, companyId: ids[0] });
      }

      triggerRefetch();
      onCloseModalHandler();
      snackbarConstructor("Tags assigned successfully", "normal");
    } catch (e) {
      const axiosError = e as AxiosError<{ message: any }>;
      reportErrorToSentry(axiosError, "ADD COMPANIES TAGS MODAL");
      snackbarConstructor("Error while assigning tags", "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function removeTagsHandler(tag: string) {
    if (newTags.includes(tag)) {
      return setNewTags((prevTags) => prevTags.filter((t) => t !== tag));
    } else if (oldTags.includes(tag)) {
      return setOldTagsToDelete((prevTags) => [...prevTags, tag]);
    } else {
      console.log("Error while removing tag"); // for test
    }
  }

  function onCloseModalHandler() {
    setTagInput("");
    setInputError("");
    setNewTags([]);
    setOldTagsToDelete([]);
    onClose();
    setIsDiscardModalOpen(false);
  }

  function onChangeInputHandler(value: string) {
    setInputError("");
    setTagInput(value);
  }

  function onSelectHandler(value: string) {
    if (oldTagsToDelete.includes(value)) {
      setOldTagsToDelete((prev) => prev.filter((tag) => tag !== value));
    } else {
      setNewTags((prev) => [...prev, value]);
    }
    setTagInput("");
    setInputError("");
  }

  function onCloseDropdownHandler() {
    setTagInput("");
    setInputError("");
  }

  function handleCancel() {
    if (newTags.length > 0 || oldTagsToDelete.length > 0) {
      setIsDiscardModalOpen(true);
    } else {
      onCloseModalHandler();
    }
  }

  return (
    <div>
      <Modal isOpen={isOpen} onClose={handleCancel} title="Tags" isHeader minHeight="240px">
        <div>
          <div>
            <DropdownInput
              value={tagInput}
              onChange={(e) => onChangeInputHandler(e.target.value)}
              options={organizationTags.filter(
                (tag) => !newTags?.includes(tag.value) && !displayedOldTags.includes(tag.value)
              )}
              onSelect={(value) => onSelectHandler(value)}
              placeholder="Add tags"
              onClose={onCloseDropdownHandler}
              error={inputError}
            />
          </div>
          <Typography variant="body-3" className={clsx(inputError && styles.inputErrorMargin)}>
            Press enter to add a tag
          </Typography>

          <div className={styles.tagList}>
            {displayedOldTags.map((tag, index) => {
              return (
                <Tag
                  onClose={() => {
                    removeTagsHandler(tag);
                  }}
                  key={index}
                >
                  {tag}
                </Tag>
              );
            })}
            {newTags?.map((tag, index) => {
              return (
                <Tag
                  onClose={() => {
                    removeTagsHandler(tag);
                  }}
                  key={index}
                >
                  {tag}
                </Tag>
              );
            })}
          </div>
        </div>
        <div className={styles.optionContainer}>
          <CancelButton size="large" onClick={handleCancel}>
            Cancel
          </CancelButton>
          <NormalButton size="large" onClick={assignTagsHandler} isDisabled={isLoading}>
            Save
          </NormalButton>
        </div>
      </Modal>
      <DiscardChangesModal
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onDiscard={onCloseModalHandler}
      />
    </div>
  );
}
